import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '../../hooks/use-document-title';
import {
  ContentAndButtonsContainer,
  NavbarAndContentContainer,
} from '../../layout/layout-container.styled';
import { listItemMotion, Presence, Staleable, StateSwitch } from '../../../motion';
import { ListSkeleton } from '../dashboard/dashboard-shared-components/load-state';
import { ScreenHeader } from '../../layout/page-header';
import { DefaultComponentProps } from '../../app-router/component-props';
import {
  ActivityCloseError,
  OpenActivity,
  closeActivity,
  getOpenActivities,
} from '../../../api/daily-log-client';
import { useAuth } from '../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../auth/permissions';
// THE STYLES ARE THE VISITOR EXIT'S, IMPORTED RATHER THAN COPIED.
//
// This screen and "Coming out" are the same act on a different table: a list of
// things that are still open, a search over it, and one confirmed press per row.
// A second stylesheet would be two descriptions of one control that drift the
// first time either is touched — and the design system's whole point is that a
// row on one desk screen looks like a row on the next.
import {
  Actions,
  Banner,
  BannerDismiss,
  Body,
  Card,
  CardTitle,
  Confirm,
  ConfirmText,
  Empty,
  ExitItem,
  Hint,
  Input,
  Label,
  Pill,
  Row,
  RowAction,
  RowLead,
  RowMain,
  RowMark,
  RowMeta,
  RowName,
  SearchRow,
  Stat,
  Stats,
  bannerMotion,
  confirmMotion,
} from '../visitor-exit/visitor-exit-page.styled';
import { initialsOf } from '../visitor-exit/desk-list';
import { dayKeyIn, formatIn, todayIn } from '../../../function-utils/building-time';

// ---------------------------------------------------------------------------
// BUILDING ACTIVITIES — OUT.
// ---------------------------------------------------------------------------
//
// The fourth pair on the main menu got its second half in migration 079. Before
// it, `daily_log` recorded that something STARTED and had nowhere to record that
// it ENDED, so the desk wrote a second entry — "maintenance crew left at 18:00"
// as a fresh Miscellaneous note — which is a different row, at a different time,
// with nothing tying it to the first. Nothing could count it, the handover
// reported two events where one happened, and "is the lift engineer still in the
// building" was unanswerable from a table holding the answer twice.
//
// ============ WHAT THIS SCREEN DOES NOT DO ============
//
// NO SCAN. The visitor exit has one because a departing person can present a
// document that narrows a list of names. An activity is not a person and has no
// document; a scan button here would be an affordance with nothing behind it.
//
// NO EDIT. Closing is not editing. The close columns are not client-writable at
// all (daily-log.js pins them on create and strips them on update) and the only
// writer is POST /daily-log/:id/close, which stamps now() and the authenticated
// user. There is nothing on this screen a caller can steer, which is what makes
// close_source='desk' mean "a human observed this end".
/* Layout animation (survivors sliding up when one leaves) is for a list you
   can read at a glance; past this many rows the reflow costs more than it
   says, so the rows still fade but stop measuring each other. */
const LAYOUT_CAP = 60;

const CLOSE_HINT =
  'Closing an activity records that it finished, now, and who was at the desk. ' +
  'If you did not see it end, leave it open — an activity nobody closed is ' +
  'reported to the next shift as still running, which is the truth.';

/** Newest first, and the server already sorted it that way — this only narrows. */
const filterActivities = (rows: OpenActivity[], query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return rows;

  return rows.filter(row =>
    [row.buildingActivity, row.apartmentLocation, row.details, row.priority]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q)
  );
};

/** "Maintenance — Unit 10", or just the kind when no unit was recorded. */
const describeActivity = (row: OpenActivity): string =>
  [row.buildingActivity, row.apartmentLocation ? `Unit ${row.apartmentLocation}` : '']
    .filter(Boolean)
    .join(' — ');

/** Started at, IN THE BUILDING'S ZONE.
 *
 *  This rendered in the device's locale on the reasoning that the row carries no
 *  zone of its own. The ROW does not, but the BUILDING does — it is on
 *  `selectedBuilding.timezone` and this screen already holds it. A desk closing
 *  an activity has to match what it sees here against what it saw when the
 *  activity started, and a device clock is only the same clock by coincidence.
 *
 *  Degrades to the reader's zone rather than to UTC when a building has none:
 *  treating an absent zone as Greenwich is exactly what made 09:24 print as
 *  07:24. */
const startedAt = (iso: string, tz?: string | null): string =>
  // An unusable zone does not take the screen down: the one time module falls
  // back to the device's clock.
  formatIn(iso, tz, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });

/** An activity open since before today. Same idea as the visitor exit's stale
 *  arrivals, and the same honesty rule: it is a PROCESS GAP, not evidence that
 *  anybody is still on site. */
const isStale = (row: OpenActivity, tz?: string | null): boolean => {
  const started = new Date(row.createdAt);
  if (Number.isNaN(started.getTime())) return false;
  // CUT IN THE SAME ZONE IT IS RENDERED IN. `setHours(0,0,0,0)` is midnight on
  // the DEVICE, so a desk two hours east of its own building would mark this
  // morning's activity as "open since an earlier day" for two hours every
  // night. Compared as formatted dates rather than by arithmetic on instants,
  // which is what keeps it right for a zone whose offset is not a whole hour.
  // Day keys from the one time module: an unusable zone falls back there.

  return dayKeyIn(started, tz) !== todayIn(tz);
};

// The rail, the building panel and <main> come from DeskShell now
// (layout/desk-shell.tsx); this screen renders only what goes inside the card.
export const ActivityExitPage: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
}) => {
  useDocumentTitle('Activities out');

  const { permissions }: { permissions: string[] } = useAuth();
  // The SAME two permissions the server checks. dailylog:create is what the
  // close route is gated on — recording that an activity ended is the same class
  // of act as recording that it started.
  const canClose = can(permissions, PERMISSIONS.DAILYLOG_CREATE);
  const allowedToList = can(permissions, PERMISSIONS.DAILYLOG_READ);

  const buildingId = selectedBuilding ? Number(selectedBuilding.id) : NaN;
  // The building's own clock. Every time on this screen renders in it.
  const tz = selectedBuilding?.timezone || undefined;

  const [rows, setRows] = useState<OpenActivity[] | null>(null);
  const [loading, setLoading] = useState(false);
  /* PER ACTIVITY, NOT ONE AT A TIME — and the ref, not the state, is the gate.
     Identical to "Coming out" (visitor-exit-page.tsx) and for identical reasons:
     a single boolean disabled every row while one request was in the air, and
     two presses inside one React commit both read it as false, so the second
     one still sent. Two writes for one close come back 409 ALREADY_CLOSED and
     the desk is told a colleague closed it — about their own first tap. */
  const [pending, setPending] = useState<ReadonlySet<number>>(() => new Set());
  const inFlight = useRef<Set<number>>(new Set());
  /* An older read must not overwrite a newer one. refresh() runs on mount, after
     every close and after every 409. */
  const readSeq = useRef(0);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    tone: 'success' | 'warning';
    text: string;
  } | null>(null);

  const refresh = useCallback(async () => {
    if (!Number.isInteger(buildingId) || !allowedToList) {
      setRows([]);

      return;
    }
    const seq = ++readSeq.current;
    setLoading(true);
    try {
      const next = await getOpenActivities(buildingId);
      if (seq !== readSeq.current) return;
      setRows(next);
      setError(null);
    } catch {
      if (seq !== readSeq.current) return;
      // NEVER RENDER A FAILED LOAD AS AN EMPTY DESK. `rows` stays null so the
      // list shows nothing rather than "everything is closed" — the RC-10 class
      // of bug, where an error looks exactly like a quiet day.
      setRows(null);
      setError('We could not load the open activities just now.');
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [buildingId, allowedToList]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /* A SUCCESS CONFIRMS AND THEN GOES.
     This banner used to be cleared only by the NEXT close, so on a desk screen
     left open all shift it reported an action from hours ago as though it had
     just happened. The warning tone is deliberately excluded: it says the row
     was already closed by somebody else and the press changed nothing, and that
     has to survive the reader looking down at the list and back up. */
  useEffect(() => {
    if (notice?.tone !== 'success') return undefined;
    const timer = setTimeout(() => setNotice(null), 6000);

    return () => clearTimeout(timer);
  }, [notice]);

  const submitClose = async (row: OpenActivity) => {
    if (inFlight.current.has(row.id)) return;
    inFlight.current.add(row.id);
    setPending(prev => new Set(prev).add(row.id));
    setError(null);
    setNotice(null);
    try {
      await closeActivity(row.id);
      setNotice({
        tone: 'success',
        text: `${describeActivity(row)} closed.`,
      });
      setConfirming(null);
      await refresh();
    } catch (e) {
      const err = e as ActivityCloseError;
      // A 409 is not a failure the desk caused. Somebody else closed it, or the
      // sweep did — and those are different things to be told, so the server's
      // own close_source decides the wording rather than a guess here.
      if (err.code === 'ALREADY_CLOSED') {
        setNotice({
          tone: 'warning',
          text:
            err.closeSource === 'desk'
              ? 'Somebody at the desk already closed this activity. Nothing was changed.'
              : 'This activity was already closed automatically at the end of the day. Nothing was changed.',
        });
        setConfirming(null);
        await refresh();

        return;
      }
      setError(err.message || 'Could not close the activity.');
    } finally {
      inFlight.current.delete(row.id);
      setPending(prev => {
        const next = new Set(prev);
        next.delete(row.id);

        return next;
      });
    }
  };

  const visible = rows ? filterActivities(rows, query) : [];
  const count = rows ? rows.length : 0;
  const staleCount = rows ? rows.filter(r => isStale(r, tz)).length : 0;
  // Narrowed vs total, shown only when they differ — "7 of 7" is a sentence
  // that says nothing and still costs a tile.
  const narrowed = query.trim() ? visible.length : null;

  // ONE WORD FOR WHAT THE LIST IS, keying the crossfade below. `rows === null`
  // is "nothing has answered yet": a skeleton, unless the read failed — then
  // the banner above has said so and the box stays empty rather than looking
  // like a quiet day (the RC-10 class of bug). A refresh keeps the rows and
  // dims them.
  const listState = !allowedToList
    ? 'denied'
    : rows === null
      ? error
        ? 'failed'
        : 'loading'
      : rows.length === 0
        ? 'empty'
        : visible.length === 0
          ? 'nomatch'
          : 'ready';

  const renderList = () => {
    if (listState === 'loading') {
      return <ListSkeleton rows={3} label="the open activities" />;
    }

    if (listState === 'failed') return null;

    if (!allowedToList) {
      return (
        <Empty>
          You can close an activity, but you cannot see the list of what is open
          — that needs the building-activities read permission. Ask an owner or
          property manager on your team to grant it.
        </Empty>
      );
    }

    if (rows && rows.length === 0) {
      return <Empty>Nothing is open. Every activity here has been closed.</Empty>;
    }

    if (visible.length === 0) {
      return (
        <Empty>
          No open activity matches “{query}”. It may already have been closed, or
          it was logged under a different category.
          {/* THE WAY OUT, WHERE THE READER IS LOOKING. The miss was explained
              and then left in place: clearing it meant going back up to the
              search box and deleting the query by hand. */}
          <RowAction
            type="button"
            variant="quiet"
            onClick={() => setQuery('')}
          >
            Clear search
          </RowAction>
        </Empty>
      );
    }

    // Rows arrive in order and, in the Presence, a closed activity leaves
    // rather than vanishing; layout stays on while the list is short enough
    // for the survivors' slide to read.
    return (
      <Staleable stale={loading}>
        <Presence>
          {visible.map((row, index) => {
      const stale = isStale(row, tz);
      const tone = stale ? 'stale' : 'plain';

      return (
        <ExitItem
          key={row.id}
          {...listItemMotion(index)}
          layout={visible.length <= LAYOUT_CAP}
        >
          <Row tone={tone}>
            <RowLead>
              {/* The CATEGORY's initial, not a person's — an activity has no
                  name to take initials from, and the category is what the desk
                  is scanning the column for. */}
              <RowMark tone={tone} aria-hidden="true">
                {/* The other three lists that render this component all use
                    initialsOf; this one took a single character and so drew the
                    same disc for every activity starting with the same letter. */}
                {initialsOf(row.buildingActivity || '')}
              </RowMark>
              <RowMain>
                <RowName>{describeActivity(row)}</RowName>
                <RowMeta>
                  {/* PRIORITY IS SHOWN. It is selected by the server, typed on
                      the client and already part of the search haystack above —
                      and it was the one thing never rendered. A High-priority
                      maintenance activity sat in this list looking exactly like
                      a routine one, on the screen whose whole job is deciding
                      what still needs closing before a handover. */}
                  {[
                    row.priority ? `${row.priority} priority` : '',
                    startedAt(row.createdAt, tz),
                    row.details || '',
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </RowMeta>
                {stale ? (
                  <Pill tone="warning">Open since an earlier day</Pill>
                ) : null}
              </RowMain>
            </RowLead>
            <Actions>
              <RowAction
                type="button"
                variant={confirming === row.id ? 'quiet' : 'primary'}
                disabled={!canClose || pending.has(row.id)}
                onClick={() =>
                  setConfirming(confirming === row.id ? null : row.id)
                }
              >
                {confirming === row.id ? 'Cancel' : 'Close'}
              </RowAction>
            </Actions>
          </Row>
          <Presence>
            {confirming === row.id ? (
              <Confirm key="confirm" {...confirmMotion}>
                <ConfirmText>
                  {stale
                    ? `Record that ${describeActivity(row)} finished? It was started on ${startedAt(row.createdAt, tz)} and nobody closed it — if you did not see it end, leave it open.`
                    : `Record that ${describeActivity(row)} finished, now?`}
                </ConfirmText>
                <RowAction
                  type="button"
                  variant="danger"
                  disabled={pending.has(row.id)}
                  onClick={() => submitClose(row)}
                >
                  {pending.has(row.id) ? 'Recording…' : 'Confirm close'}
                </RowAction>
              </Confirm>
            ) : null}
          </Presence>
        </ExitItem>
      );
          })}
        </Presence>
      </Staleable>
    );
  };

  return (
    <NavbarAndContentContainer>
          <ContentAndButtonsContainer>
            <Body>
              <ScreenHeader
                title="Activities out"
                subtitle={
                  selectedBuilding?.property_name || 'No building selected'
                }
              />

              <Presence>
                {error ? (
                  <Banner key="error" tone="alert" role="alert" {...bannerMotion}>
                    {error}
                  </Banner>
                ) : null}
                {notice ? (
                  <Banner
                    key="notice"
                    tone={notice.tone}
                    role="status"
                    {...bannerMotion}
                  >
                  {notice.text}
                  {/* A WAY OUT THAT DOES NOT DEPEND ON WAITING. The warning has
                      no timer by design, so without this it stayed until the
                      next close attempt - which is exactly the press a reader
                      who has just been told "nothing was changed" is least
                      likely to make. */}
                  <BannerDismiss
                    type="button"
                    aria-label="Dismiss this message"
                    onClick={() => setNotice(null)}
                  >
                    ×
                  </BannerDismiss>
                  </Banner>
                ) : null}
              </Presence>

              <Card>
                <CardTitle>Still open</CardTitle>
                <Hint>{CLOSE_HINT}</Hint>
                {/* The same three tiles as "Coming out", in the same order and
                    the same shape — a desk should not learn two ways to read
                    the same three numbers. */}
                <Stats>
                  <Stat tone={count > 0 ? 'brand' : 'plain'}>
                    <b>{count}</b>
                    <span>Still open</span>
                  </Stat>
                  <Stat tone={staleCount > 0 ? 'warning' : 'plain'}>
                    <b>{staleCount}</b>
                    <span>From earlier days</span>
                  </Stat>
                  <Stat>
                    <b>{narrowed ?? count}</b>
                    <span>{narrowed === null ? 'Showing' : 'Matching'}</span>
                  </Stat>
                </Stats>
                {/* The stale count sits BESIDE the total, never subtracted from
                    it. Both numbers are true and they answer different
                    questions: how many activities are open, and how many of
                    those have been open since before today. */}
                {/* Part of the first paint, so it does not animate in on
                    top of the page transition (initial={false}). */}
                <Presence initial={false}>
                  {staleCount > 0 ? (
                    <Banner key="stale" tone="warning" role="status" {...bannerMotion}>
                      {staleCount === 1
                        ? '1 of these was started on an earlier day and never closed. It is not evidence that anybody is still on site.'
                        : `${staleCount} of these were started on earlier days and never closed. They are not evidence that anybody is still on site.`}
                    </Banner>
                  ) : null}
                </Presence>
                <SearchRow>
                  <Label htmlFor="activity-exit-search">Search</Label>
                  <Input
                    id="activity-exit-search"
                    value={query}
                    placeholder="Category, unit or note"
                    onChange={e => {
                      setQuery(e.target.value);
                      setConfirming(null);
                    }}
                  />
                </SearchRow>
                {/* Skeleton, the explanations and the rows are fills of ONE
                    box that crossfade; the floor is three skeleton rows. */}
                <StateSwitch state={listState} minHeight={192}>
                  {renderList()}
                </StateSwitch>
              </Card>
            </Body>
          </ContentAndButtonsContainer>
    </NavbarAndContentContainer>
  );
};
